import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import LegalGate from "../legal/LegalGate";
import { consentPayload } from "../legal/legalConfig";
import { logoutClient, signOut } from "../lib/endSession";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const api = axios.create({ baseURL: API, withCredentials: true });
// Signup boxes ticked before the Google redirect; AuthCallback sends them with the session.
export const SIGNUP_CONSENT_KEY = "nimbus_signup_consent";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true, timeout: 10000 })  /* a hung /auth/me must not leave pages (e.g. /support) stuck on Loading */;
      setUser(res.data);
      return res.data;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // CRITICAL: if returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  useEffect(() => {
    // Back-forward cache can restore a signed-in page. Ask the server again; a revoked token clears it.
    const onShow = (event) => {
      if (event.persisted) refresh();
    };
    window.addEventListener("pageshow", onShow);
    return () => window.removeEventListener("pageshow", onShow);
  }, [refresh]);

  // Email/password
  // consent = { terms, privacy } from the two required signup checkboxes.
  const signUp = async (email, password, name, consent) => {
    // Sign-up doesn't sign in: the reply is the same whether or not the email already has an account.
    // The emailed confirmation link signs the new account in (VerifyEmail page).
    const res = await axios.post(`${API}/auth/register`, { email, password, name, ...consentPayload(consent || {}) },
      { withCredentials: true });
    return res.data;
  };

  const signIn = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    if (res.data?.requires_2fa) {
      return res.data;
    }
    setUser(res.data.user);
    refresh();
    return res.data;
  };

  const completeCustomer2fa = async (pending, code) => {
    const res = await axios.post(`${API}/auth/2fa`, { pending_2fa: pending, code }, { withCredentials: true });
    setUser(res.data.user);
    refresh();
    return res.data;
  };

  // Google (Emergent) OAuth redirect
  const googleLogin = (consent) => {
    try {
      if (consent?.terms && consent?.privacy) sessionStorage.setItem(SIGNUP_CONSENT_KEY, JSON.stringify(consent));
      else sessionStorage.removeItem(SIGNUP_CONSENT_KEY);
    } catch {}
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/workspace";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = ({ navigate } = {}) => signOut({
    post: (path, body, config) => logoutClient().post(`${API}${path}`, body, config),
    setUser,
    navigate,
  });

  return (
    <AuthContext.Provider value={{ user, loading, refresh, signUp, signIn, completeCustomer2fa, googleLogin, logout }}>
      {children}
      <LegalGate api={api} user={user} onSignOut={logout} />
    </AuthContext.Provider>
  );
};

export default AuthProvider;
